"use client";

import { createContext, useContext, useCallback, useState, type ReactNode } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "../providers/AuthProvider";
import { useIsMobile } from "../hooks/useIsMobile";
import { ProfileDropdown } from "./ProfileDropdown";

// ─── Sidebar toggle context ─────────────────────────────────────────────────
// ChatLayout registers its toggle callback here so the layout-level header
// can drive it.

interface HeaderActionContextValue {
  sidebarCollapsed: boolean;
  onToggleSidebar: (() => void) | null;
  register: (collapsed: boolean, toggle: () => void) => void;
  unregister: () => void;
  updateCollapsed: (collapsed: boolean) => void;
  /** Channel name to display in the header on mobile */
  channelName: string | null;
  setChannelName: (name: string | null) => void;
  /** Callback to toggle the members panel (used on mobile) */
  onToggleMembers: (() => void) | null;
  setToggleMembers: (fn: (() => void) | null) => void;
  /** Unread status for the hamburger badge */
  unreadBadge: "none" | "unread" | "working";
  setUnreadBadge: (status: "none" | "unread" | "working") => void;
}

const HeaderActionContext = createContext<HeaderActionContextValue>({
  sidebarCollapsed: true,
  onToggleSidebar: null,
  register: () => {},
  unregister: () => {},
  updateCollapsed: () => {},
  channelName: null,
  setChannelName: () => {},
  onToggleMembers: null,
  setToggleMembers: () => {},
  unreadBadge: "none",
  setUnreadBadge: () => {},
});

export function useHeaderAction() {
  return useContext(HeaderActionContext);
}

export function HeaderActionProvider({ children }: { children: ReactNode }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [toggle, setToggle] = useState<(() => void) | null>(null);
  const [channelName, setChannelName] = useState<string | null>(null);
  const [onToggleMembers, setToggleMembers] = useState<(() => void) | null>(null);
  const [unreadBadge, setUnreadBadge] = useState<"none" | "unread" | "working">("none");

  const register = useCallback((collapsed: boolean, fn: () => void) => {
    setSidebarCollapsed(collapsed);
    setToggle(() => fn);
  }, []);

  const unregister = useCallback(() => {
    setToggle(null);
    setSidebarCollapsed(true);
  }, []);

  const updateCollapsed = useCallback((collapsed: boolean) => {
    setSidebarCollapsed(collapsed);
  }, []);

  return (
    <HeaderActionContext.Provider
      value={{ sidebarCollapsed, onToggleSidebar: toggle, register, unregister, updateCollapsed, channelName, setChannelName, onToggleMembers, setToggleMembers, unreadBadge, setUnreadBadge }}
    >
      {children}
    </HeaderActionContext.Provider>
  );
}

// ─── Header ─────────────────────────────────────────────────────────────────

export function AppHeader() {
  const { ready, authenticated } = useAuth();
  const pathname = usePathname();
  const router = useRouter();
  const { sidebarCollapsed, onToggleSidebar, channelName, onToggleMembers, unreadBadge } = useHeaderAction();
  const isMobile = useIsMobile();

  const isDashboard = pathname.startsWith("/dashboard");
  const isAbout = pathname.startsWith("/about");
  const isSubpage = isDashboard || isAbout;

  // Left button: dot (logged out), back (dashboard/about), hamburger (home logged in)
  let leftButton: React.ReactNode;

  if (!ready || !authenticated) {
    // Non-interactive dot
    leftButton = (
      <span style={leftBtnBase}>
        ·
      </span>
    );
  } else if (isSubpage) {
    // Back arrow
    leftButton = (
      <button
        onClick={() => router.push("/")}
        style={{ ...leftBtnBase, cursor: "pointer" }}
        title="Back to home"
      >
        ‹
      </button>
    );
  } else {
    // Hamburger / minus for sidebar toggle
    leftButton = (
      <button
        onClick={onToggleSidebar ?? undefined}
        style={{
          ...leftBtnBase,
          cursor: onToggleSidebar ? "pointer" : "default",
          position: "relative",
          // 44px minimum touch target for mobile
          width: 44,
          height: 44,
        }}
        title={sidebarCollapsed ? "Show channels" : "Hide channels"}
      >
        {sidebarCollapsed ? "☰" : <span style={{ position: "relative", top: 2 }}>−</span>}
        {sidebarCollapsed && unreadBadge !== "none" && (
          <span
            style={{
              position: "absolute",
              top: 8,
              right: 6,
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: unreadBadge === "working" ? "#d4a017" : "#dc2626",
            }}
          />
        )}
      </button>
    );
  }

  return (
    <header
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 0.5rem",
        height: 48,
        borderBottom: "1px solid var(--color-border)",
        background: "var(--color-bg)",
        flexShrink: 0,
        overflow: "hidden",
      }}
    >
      {/* Left slot */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.25rem", minWidth: 0, flex: "0 0 auto" }}>
        {leftButton}
      </div>
      {/* Center slot — channel name */}
      {authenticated && !isSubpage && channelName && (
        <span
          onClick={onToggleMembers ?? undefined}
          style={{
            flex: 1,
            fontSize: "0.95rem",
            fontWeight: 600,
            color: "var(--color-text)",
            lineHeight: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "center",
            cursor: onToggleMembers ? "pointer" : "default",
          }}
        >
          {channelName}
        </span>
      )}
      {/* Spacer when no channel name */}
      {(!authenticated || isSubpage || !channelName) && <div style={{ flex: 1 }} />}
      {/* Right slot */}
      <div style={{ display: "flex", alignItems: "center", flex: "0 0 auto" }}>
        {authenticated && <ProfileDropdown />}
      </div>
    </header>
  );
}

const leftBtnBase: React.CSSProperties = {
  background: "none",
  border: "none",
  color: "var(--color-text-muted)",
  fontSize: "1.1rem",
  padding: 0,
  lineHeight: 1,
  marginBottom: 0,
  width: "1.1rem",
  textAlign: "center",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};
